import * as React from "react";
import { User } from "lucide-react";

type AvatarProps = {
    name?: string;
    size?: number | string;
    round?: boolean;
    className?: string;
};

// Simple deterministic hash
function hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash |= 0; // Convert to 32bit integer
    }
    return Math.abs(hash);
}

function getInitials(name?: string): string {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// Pick from a fixed palette
const COLORS = [
    "#f87171", // red-400
    "#fb923c", // orange-400
    "#facc15", // yellow-400
    "#4ade80", // green-400
    "#2dd4bf", // teal-400
    "#60a5fa", // blue-400
    "#a78bfa", // purple-400
    "#f472b6", // pink-400
    "#94a3b8" // slate-400
];

export const Avatar = ({ name, size = 40, round = true, className = "" }: AvatarProps): React.ReactElement => {
    const initials = getInitials(name);
    const px = typeof size === "number" ? `${size}px` : size; // prefer passing a number
    const colorIndex = name ? hashCode(name) % COLORS.length : 0;
    const backgroundColor = COLORS[colorIndex];

    return (
        <div
            className={`avatar ${className}`}
            style={{
                // prevent parent flex from stretching it
                display: "inline-flex",
                flex: "0 0 auto",

                // fixed square box
                width: px,
                height: px,
                minWidth: px,
                minHeight: px,
                aspectRatio: "1 / 1",

                // look
                borderRadius: round ? "50%" : "8px",
                backgroundColor,
                color: "white",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 600,
                lineHeight: "normal",
                fontSize: "0.9em",
                boxSizing: "border-box",
                overflow: "hidden"
            }}
            title={name}
        >
            {initials ? initials : <User size={typeof size === "number" ? size * 0.6 : 20} />}
        </div>
    );
};
